import { Box, Flex, HStack, Icon, Skeleton, Text } from '@chakra-ui/react'
import { format } from 'date-fns'
import { Calendar } from 'emotion-icons/feather'
import { gql } from 'urql'
import { Application } from 'entities/application'
import { Tag } from 'entities/tag'
import { tags as mockTags } from 'entities/tag.mock'
import { FileOrLink, User } from 'entities/user'
import { FormatString } from 'lib/date'
import { CardWrapper } from 'components/CardWrapper'
import { ActionButtons } from 'components/application/ApplicationCardCandidate/ActionButtons'
import { ApplicationStatus } from 'components/application/ApplicationCardCandidate/ApplicationStatus'
import { Attachments } from 'components/application/ApplicationCardCandidate/Attachments'
import {
    FRAGMENT_JOB_INFO,
    JobInfo,
    JobInfoData,
} from 'components/application/ApplicationCardCandidate/JobInfo'
import { Tags } from 'components/tags/Tags'

export type CandidateApplicationBasicData = Pick<
    Application,
    'state' | 'conversationId' | 'timestamp' | 'jobId' | 'id'
> & {
    coverLetter?: Pick<FileOrLink, 'file'>
    user: Pick<User, 'id'> & {
        cv: FileOrLink
        skills: { tag: Pick<Tag, 'id' | 'label'> }[]
    }
}

// I tried to share common fields between the two fragments, but it didn't work
export const FRAGMENT_CANDIDATE_APPLICATION_BASIC = gql<CandidateApplicationBasicData>`
    fragment CandidateApplicationBasic on Application {
        id
        state
        conversationId
        timestamp
        jobId
        coverLetter {
            file {
                url
            }
        }
        user {
            id
            cv {
                link
                file {
                    url
                }
            }
            skills {
                tag {
                    id
                    label
                }
            }
        }
    }
`

export type CandidateApplicationFullData = CandidateApplicationBasicData & {
    job: JobInfoData
}

export const FRAGMENT_CANDIDATE_APPLICATION_FULL = gql<CandidateApplicationFullData>`
    ${FRAGMENT_JOB_INFO}
    fragment CandidateApplicationFull on Application {
        id
        state
        conversationId
        timestamp
        jobId
        coverLetter {
            file {
                url
            }
        }
        user {
            id
            cv {
                link
                file {
                    url
                }
            }
            skills {
                tag {
                    id
                    label
                }
            }
        }
        job {
            ...JobInfo
        }
    }
`

const fakeTags = [mockTags[0], mockTags[1], mockTags[2]]

export const ApplicationCardCandidate = ({
    onUpdate,
    variant,
    application,
}:
    | {
          variant: 'full'
          application: CandidateApplicationFullData
          onUpdate?: () => void
      }
    | {
          variant: 'basic'
          application: CandidateApplicationBasicData
          onUpdate?: () => void
      }) => {
    const isLoaded = Boolean(application?.jobId)
    const tags = application?.user?.skills?.map(skill => skill.tag) || fakeTags

    const isFull = variant === 'full'
    const { state, timestamp } = application ?? {}

    return (
        <Box
            minW={isFull ? { base: 'full', md: '376px' } : '100%'}
            as={CardWrapper}
            pos="relative"
            pb="26px"
            borderRadius="16px"
        >
            <ApplicationStatus status={state} isLoaded={isLoaded} />

            {variant === 'full' && <JobInfo isLoaded={isLoaded} job={application?.job} />}

            {/* content */}
            <Flex h="full" flexDir="column" mt="26px">
                {/* Date applied */}
                <HStack>
                    <Icon as={Calendar} fontSize="18px" strokeWidth="1.5px" />

                    <Skeleton isLoaded={isLoaded}>
                        <Text
                            w="full"
                            variant={isFull ? 'functionalButtonsSmall' : 'bodyTextSmall'}
                            fontWeight="semibold"
                        >
                            Applied on{' '}
                            {timestamp
                                ? format(
                                      new Date(timestamp),
                                      FormatString.ShortLocalDate_Year,
                                  )
                                : 'Text for skeleton'}
                        </Text>
                    </Skeleton>
                </HStack>

                {/* Attachments */}
                <Attachments
                    isLoaded={isLoaded}
                    isFull={isFull}
                    cv={application?.user?.cv}
                    coverLetter={application?.coverLetter}
                />

                {/* Skills */}
                <Box mb="auto" mt={isFull ? '16px' : '20px'}>
                    {!isFull && (
                        <Text
                            variant="bodyTextSmall"
                            fontWeight="medium"
                            color="text.secondary"
                            mb="8px"
                        >
                            Your skills
                        </Text>
                    )}

                    <Tags
                        isLoaded={isLoaded}
                        tags={tags}
                        variant="secondary"
                        size="md"
                        maxH="72px"
                    />
                </Box>

                {/* Action Buttons */}
                {/*<ActionButtons*/}
                {/*    isLoaded={isLoaded}*/}
                {/*    isFull={isFull}*/}
                {/*    onUpdate={onUpdate}*/}
                {/*    application={application}*/}
                {/*/>*/}
            </Flex>
        </Box>
    )
}
